import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  IconButton,
  Collapse,
  Chip,
  Checkbox,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Public as PublicIcon,
  Circle as CircleIcon,
  PushPin as PushPinIcon,
  FilterAlt as FilterAltIcon,
  CheckBox as CheckBoxIcon,
  Bolt as BoltIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorOutlineIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { Region } from '../../services/api';
import { queryPrometheusInstantVector } from '../../services/grafana';
import SmartFilterBar, { SmartFilterBarHandle, normalizeFilterQueryForCompare } from './SmartFilterBar';

interface RegionsPanelProps {
  regions: Region[];
  selectedRegions: string[];
  tenantId?: string | null;
  scenarioId?: string | null;
  regionRunningCounts?: Record<string, number>;
  regionCapacityCounts?: Record<string, number>;
  isCollapsed: boolean;
  onToggle: () => void;
  onRegionSelectionChange: (regions: string[]) => void;
  onShutdownRegions: (regions: string[]) => void;
  onTerminateRegions: (regions: string[]) => void;
  isActionLoading?: boolean;
  isAdvancedMode?: boolean;
}

type SortField = 'id' | 'provider' | 'bots';
type SortOrder = 'asc' | 'desc';

const RegionsPanel: React.FC<RegionsPanelProps> = ({
  regions,
  selectedRegions,
  tenantId,
  scenarioId,
  regionRunningCounts,
  regionCapacityCounts,
  isCollapsed,
  onToggle,
  onRegionSelectionChange,
  onShutdownRegions,
  isActionLoading = false,
  isAdvancedMode = false
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const filterBarRef = useRef<SmartFilterBarHandle>(null);
  const [filteredRegions, setFilteredRegions] = useState<Region[]>(regions);
  const [activeFilterQueries, setActiveFilterQueries] = useState<string[]>([]);

  const isFilterActive = useCallback((query: string) => {
    const n = normalizeFilterQueryForCompare(query);
    return activeFilterQueries.some((q) => normalizeFilterQueryForCompare(q) === n);
  }, [activeFilterQueries]);
  const [sortField, setSortField] = useState<SortField>('bots');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [promBotsByRegion, setPromBotsByRegion] = useState<Record<string, number>>({});
  const hasExternalCounts = Boolean(regionRunningCounts && Object.keys(regionRunningCounts).length > 0);

  // Unique key is provider_id + region_id (same region_id can exist for different providers)
  const getRegionBotCount = useCallback((region: Region): number => {
    const baseCount = Number.isFinite(region.machine_count)
      ? Math.max(0, Math.round(region.machine_count))
      : 0;
    let bestCount = baseCount;
    const providerId = (region.provider?.id ?? '').toLowerCase();
    const candidates = [region.region_code, region.id, region.name]
      .filter((value): value is string => Boolean(value))
      .map((value) => `${providerId}:${value.toLowerCase()}`);
    const legacyCandidates = [region.region_code, region.id, region.name]
      .filter((value): value is string => Boolean(value))
      .map((value) => value.toLowerCase());

    for (const candidate of candidates) {
      if (regionRunningCounts && regionRunningCounts[candidate] !== undefined) {
        const count = Math.max(0, Math.round(regionRunningCounts[candidate]));
        if (count > bestCount) {
          bestCount = count;
        }
      }
    }
    for (const candidate of legacyCandidates) {
      if (promBotsByRegion[candidate] !== undefined) {
        const count = Math.max(0, Math.round(promBotsByRegion[candidate]));
        if (count > bestCount) {
          bestCount = count;
        }
      }
    }

    return bestCount;
  }, [promBotsByRegion, regionRunningCounts]);

  // Total capacity for the region, read from the provider-specific denominator in
  // regionCapacityCounts: active warm-pool reservations (free + claimed + assigned)
  // for Azure and GCP, or the full quota-based region capacity otherwise. Regions
  // with no matching entry resolve to 0.
  const getRegionCapacity = useCallback((region: Region): number => {
    if (!regionCapacityCounts) return 0;
    const providerId = (region.provider?.id ?? '').toLowerCase();
    const candidates = [region.region_code, region.id, region.name]
      .filter((value): value is string => Boolean(value))
      .map((value) => `${providerId}:${value.toLowerCase()}`);
    for (const candidate of candidates) {
      if (regionCapacityCounts[candidate] !== undefined) {
        return Math.max(0, Math.round(regionCapacityCounts[candidate]));
      }
    }
    return 0;
  }, [regionCapacityCounts]);

  const runningBotsCount = useMemo(
    () => regions.reduce((sum, region) => sum + getRegionBotCount(region), 0),
    [regions, getRegionBotCount]
  );
  // Total capacity across the currently filtered regions vs.
  // every region in scope — shown as "filtered / total available" in the stats bar.
  const filteredCapacity = useMemo(
    () => filteredRegions.reduce((sum, region) => sum + getRegionCapacity(region), 0),
    [filteredRegions, getRegionCapacity]
  );
  const totalCapacity = useMemo(
    () => regions.reduce((sum, region) => sum + getRegionCapacity(region), 0),
    [regions, getRegionCapacity]
  );
  const providersCount = new Set(regions.map(r => r.provider?.id).filter(Boolean)).size;
  const runningRegions = useMemo(
    () => regions.filter(region => getRegionBotCount(region) > 0),
    [regions, getRegionBotCount]
  );
  const regionsWithProblemsCount = useMemo(
    () => regions.filter(r => r.status === 'cooldown' || r.status === 'unavailable').length,
    [regions]
  );

  // Explicit multi-select: only checkbox-checked regions are selected.
  // Filters (below) only narrow which rows are visible; they no longer
  // auto-select. Selection persists even if a checked region is filtered out.
  const selectedSet = useMemo(() => new Set(selectedRegions), [selectedRegions]);

  const handleToggleRegion = useCallback((regionId: string) => {
    if (selectedSet.has(regionId)) {
      onRegionSelectionChange(selectedRegions.filter((id) => id !== regionId));
    } else {
      onRegionSelectionChange([...selectedRegions, regionId]);
    }
  }, [selectedRegions, selectedSet, onRegionSelectionChange]);

  // Always display running regions even if they don't match filter
  const pinnedRegions = useMemo(() => {
    const filteredIds = new Set(filteredRegions.map(region => region.id).filter(Boolean));
    return runningRegions.filter(region => !filteredIds.has(region.id));
  }, [runningRegions, filteredRegions]);

  const pinnedRegionIds = useMemo(() => {
    return new Set(pinnedRegions.map(region => region.id).filter(Boolean));
  }, [pinnedRegions]);

  // Default: show filtered regions; add pinned running regions below
  const displayedRegions = useMemo(() => {
    return [...filteredRegions, ...pinnedRegions];
  }, [pinnedRegions, filteredRegions]);

  useEffect(() => {
    if (!tenantId || !scenarioId || hasExternalCounts) {
      setPromBotsByRegion({});
      return;
    }

    let isActive = true;

    const fetchRegionBotCounts = async () => {
      try {
        const data = await queryPrometheusInstantVector(
          `sum by(region, cloud_region, region_code, region_id) ((time() - timestamp(network_bandwidth_down_bytes_total{tenant_id="${tenantId}",scenario_id="${scenarioId}"})) < bool 20)`
        );

        if (!isActive) return;

        const nextCounts: Record<string, number> = {};
        data.forEach((entry) => {
          const rawRegion = entry.metric?.region || entry.metric?.cloud_region || entry.metric?.region_code || entry.metric?.region_id;
          if (!rawRegion) return;
          const key = rawRegion.toLowerCase();
          nextCounts[key] = (nextCounts[key] || 0) + entry.value;
        });

        setPromBotsByRegion(nextCounts);
      } catch (error) {
        console.error('Error fetching region bot counts:', error);
      }
    };

    fetchRegionBotCounts();
    const intervalId = window.setInterval(fetchRegionBotCounts, 5000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [tenantId, scenarioId, hasExternalCounts]);

  // Sort regions
  const sortedRegions = useMemo(() => {
    const sorted = [...displayedRegions];
    
    sorted.sort((a, b) => {
      // Regions with running bots always float to the top, regardless of the
      // active sort column, so active attacks stay visible at a glance.
      const aRunning = getRegionBotCount(a) > 0;
      const bRunning = getRegionBotCount(b) > 0;
      if (aRunning !== bRunning) {
        return aRunning ? -1 : 1;
      }

      let compareValue = 0;

      switch (sortField) {
        case 'id':
          compareValue = (a.region_code || a.id || '').localeCompare(b.region_code || b.id || '');
          break;
        case 'provider':
          compareValue = (a.provider?.name || '').localeCompare(b.provider?.name || '');
          break;
        case 'bots':
          compareValue = getRegionBotCount(a) - getRegionBotCount(b);
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  }, [displayedRegions, sortField, sortOrder, getRegionBotCount]);

  // Index of the first idle (no running bots) region — used to draw a divider
  // between the pinned running block and the rest.
  const firstIdleIndex = useMemo(() => {
    return sortedRegions.findIndex((region) => getRegionBotCount(region) === 0);
  }, [sortedRegions, getRegionBotCount]);

  // Select-all operates on the currently displayed (visible) rows.
  const displayedSelectableIds = useMemo(
    () => sortedRegions.map((r) => r.id).filter((id): id is string => Boolean(id)),
    [sortedRegions]
  );
  const displayedSelectedCount = useMemo(
    () => displayedSelectableIds.filter((id) => selectedSet.has(id)).length,
    [displayedSelectableIds, selectedSet]
  );
  const allDisplayedSelected =
    displayedSelectableIds.length > 0 && displayedSelectedCount === displayedSelectableIds.length;
  const someDisplayedSelected = displayedSelectedCount > 0 && !allDisplayedSelected;

  const handleToggleSelectAll = useCallback(() => {
    if (allDisplayedSelected) {
      const displayedSet = new Set(displayedSelectableIds);
      onRegionSelectionChange(selectedRegions.filter((id) => !displayedSet.has(id)));
    } else {
      const merged = new Set(selectedRegions);
      displayedSelectableIds.forEach((id) => merged.add(id));
      onRegionSelectionChange(Array.from(merged));
    }
  }, [allDisplayedSelected, displayedSelectableIds, selectedRegions, onRegionSelectionChange]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const headerCellSx = {
    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.85 : 0.95),
    backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, isDark ? 0.1 : 0.12)} 0%, ${alpha(
      theme.palette.primary.main,
      isDark ? 0.05 : 0.08
    )} 100%)`,
    backdropFilter: 'blur(10px)',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1000,
  };

  const sortLabelSx = {
    color: `${theme.palette.primary.main} !important`,
    '&:hover': { color: `${theme.palette.primary.light} !important` },
    '& .MuiTableSortLabel-icon': { color: `${theme.palette.primary.main} !important` },
  };

  return (
    <Card
      sx={{
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.18)}`,
        transition: 'all 0.3s ease',
      }}
    >
      <CardHeader
        avatar={
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.18),
              borderRadius: '12px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.4 : 0.3)}`,
            }}
          >
            <PublicIcon sx={{ color: theme.palette.primary.main }} />
          </Box>
        }
        action={
          <IconButton onClick={onToggle}>
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        }
        title={
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Regions & Bots
          </Typography>
        }
        subheader={
          isCollapsed && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'text.secondary',
              }}
            >
              {selectedRegions.length} regions • {providersCount} providers • {runningBotsCount} running bots
            </Typography>
          )
        }
        sx={{
          '& .MuiCardHeader-content': {
            overflow: 'hidden',
          },
        }}
      />

      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <CardContent>
          {/* Stats bar (separate from filters) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                icon={<FilterAltIcon sx={{ fontSize: 18 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                      }}
                    >
                      {filteredRegions.length}/{regions.length}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      In scope
                    </Typography>
                  </Box>
                }
                sx={{
                  height: 40,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.12),
                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.28)}`,
                  '& .MuiChip-icon': { color: theme.palette.primary.main },
                }}
              />

              <Tooltip title="Total capacity (warm-pool reservations for Azure/GCP, region quota elsewhere): filtered / total available" arrow>
                <Chip
                  icon={<StorageIcon sx={{ fontSize: 18 }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 800,
                          fontSize: '1.05rem',
                          color: theme.palette.secondary.main,
                        }}
                      >
                        {filteredCapacity}/{totalCapacity}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.75rem',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Capacity
                      </Typography>
                    </Box>
                  }
                  sx={{
                    height: 40,
                    px: 1.25,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.secondary.main, isDark ? 0.14 : 0.12),
                    border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.35 : 0.28)}`,
                    '& .MuiChip-icon': { color: theme.palette.secondary.main },
                  }}
                />
              </Tooltip>

              <Chip
                icon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        color: theme.palette.info.main,
                      }}
                    >
                      {selectedRegions.length}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Selected
                    </Typography>
                  </Box>
                }
                sx={{
                  height: 40,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.info.main, isDark ? 0.14 : 0.12),
                  border: `1px solid ${alpha(theme.palette.info.main, isDark ? 0.35 : 0.28)}`,
                  '& .MuiChip-icon': { color: theme.palette.info.main },
                }}
              />

              <Chip
                icon={<BoltIcon sx={{ fontSize: 18 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        color: theme.palette.success.main,
                      }}
                    >
                      {runningRegions.length}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Active
                    </Typography>
                  </Box>
                }
                sx={{
                  height: 40,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, isDark ? 0.14 : 0.12),
                  border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.35 : 0.28)}`,
                  '& .MuiChip-icon': { color: theme.palette.success.main },
                }}
              />

              <Chip
                icon={
                  regionsWithProblemsCount > 0 ? (
                    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <WarningIcon sx={{ fontSize: 18 }} />
                  )
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        color:
                          regionsWithProblemsCount > 0
                            ? theme.palette.error.main
                            : theme.palette.warning.main,
                      }}
                    >
                      {regionsWithProblemsCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      With problems
                    </Typography>
                  </Box>
                }
                sx={{
                  height: 40,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor:
                    regionsWithProblemsCount > 0
                      ? alpha(theme.palette.error.main, isDark ? 0.14 : 0.12)
                      : alpha(theme.palette.warning.main, isDark ? 0.14 : 0.12),
                  border: `1px solid ${
                    regionsWithProblemsCount > 0
                      ? alpha(theme.palette.error.main, isDark ? 0.35 : 0.28)
                      : alpha(theme.palette.warning.main, isDark ? 0.35 : 0.28)
                  }`,
                  '& .MuiChip-icon': {
                    color:
                      regionsWithProblemsCount > 0
                        ? theme.palette.error.main
                        : theme.palette.warning.main,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Smart Filter Bar */}
          <SmartFilterBar
            ref={filterBarRef}
            regions={regions}
            onFilteredRegionsChange={setFilteredRegions}
            onActiveFiltersChange={setActiveFilterQueries}
            scenarioId={scenarioId}
          />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            {isAdvancedMode && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onShutdownRegions(selectedRegions)}
                disabled={selectedRegions.length === 0 || isActionLoading}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  borderColor: alpha(theme.palette.warning.main, 0.6),
                  color: theme.palette.warning.main,
                  '&:hover': {
                    borderColor: theme.palette.warning.main,
                    backgroundColor: alpha(theme.palette.warning.main, 0.12),
                  },
                }}
              >
                Shutdown Selected
              </Button>
            )}
            {isActionLoading && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'text.secondary',
                  alignSelf: 'center',
                }}
              >
                Applying region action...
              </Typography>
            )}
          </Box>

          {/* Sortable Region Table */}
          {sortedRegions.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                textAlign: 'center',
                py: 4,
              }}
            >
              No regions to display
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 600,
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.02 : 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.18)}`,
                borderRadius: 2,
                overflow: 'auto',
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: alpha(theme.palette.background.default, isDark ? 0.4 : 0.7),
                },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: '4px',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.6),
                  },
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={headerCellSx}>
                      <Tooltip title={allDisplayedSelected ? 'Deselect all visible' : 'Select all visible'} arrow>
                        <Checkbox
                          size="small"
                          checked={allDisplayedSelected}
                          indeterminate={someDisplayedSelected}
                          onChange={handleToggleSelectAll}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': { color: theme.palette.primary.main },
                            '&.MuiCheckbox-indeterminate': { color: theme.palette.primary.main },
                          }}
                          inputProps={{ 'aria-label': 'Select all visible regions' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === 'id'}
                        direction={sortField === 'id' ? sortOrder : 'asc'}
                        onClick={() => handleSort('id')}
                        sx={sortLabelSx}
                      >
                        Region ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === 'provider'}
                        direction={sortField === 'provider' ? sortOrder : 'asc'}
                        onClick={() => handleSort('provider')}
                        sx={sortLabelSx}
                      >
                        Provider
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <TableSortLabel
                        active={sortField === 'bots'}
                        direction={sortField === 'bots' ? sortOrder : 'asc'}
                        onClick={() => handleSort('bots')}
                        sx={sortLabelSx}
                      >
                        Bots / Capacity
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRegions.map((region, index) => {
                    const runningBots = getRegionBotCount(region);
                    const capacity = getRegionCapacity(region);
                    const capacityLabel = Number.isFinite(capacity) ? `${capacity}` : '0';
                    // Warm-pool providers (azure/gcp) count reservation units; the rest
                    // report quota-based region capacity.
                    const capacityBasis =
                      region.provider?.provider_type === 'azure' || region.provider?.provider_type === 'gcp'
                        ? 'active reservations'
                        : 'region capacity';
                    const isPinned = pinnedRegionIds.has(region.id);
                    const hasProblem = region.status === 'cooldown' || region.status === 'unavailable';
                    const problemReason =
                      (region.blocked_reason || region.cooldown_reason || region.status_reason || region.error_message)?.trim() ||
                      (region.status === 'cooldown' ? 'Cooldown' : 'Unavailable');

                    return (
                      <React.Fragment key={region.id}>
                        {firstIdleIndex > 0 && index === firstIdleIndex && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              sx={{
                                py: 1,
                                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.02 : 0.05),
                                borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.08 : 0.12)}`,
                                borderTop: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.08 : 0.12)}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flexGrow: 1, height: 1, backgroundColor: alpha(theme.palette.primary.main, 0.2) }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    color: 'text.secondary',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  standby regions
                                </Typography>
                                <Box sx={{ flexGrow: 1, height: 1, backgroundColor: alpha(theme.palette.primary.main, 0.2) }} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.05 : 0.08),
                            },
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.1 : 0.12)}`,
                          }}
                          selected={region.id ? selectedSet.has(region.id) : false}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={region.id ? selectedSet.has(region.id) : false}
                              disabled={!region.id}
                              onChange={() => {
                                if (region.id) handleToggleRegion(region.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': { color: theme.palette.primary.main },
                              }}
                              inputProps={{ 'aria-label': `Select region ${region.region_code || region.id}` }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              color: theme.palette.primary.main,
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1) },
                              ...(isFilterActive(`region_code="${region.region_code || region.id}"`)
                                ? { fontWeight: 700 }
                                : {}),
                            }}
                            onClick={() => {
                              const value = region.region_code || region.id;
                              if (value) filterBarRef.current?.addFilter(`region_code="${value}"`, String(value));
                            }}
                          >
                            <Tooltip title={isFilterActive(`region_code="${region.region_code || region.id}"`) ? 'Click to remove from filter' : 'Click to add as filter'} arrow>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isPinned && (
                                  <Tooltip title="Pinned: running" arrow>
                                    <Box
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.success.main, isDark ? 0.12 : 0.18),
                                        border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.3 : 0.35)}`,
                                      }}
                                    >
                                      <PushPinIcon sx={{ fontSize: '0.7rem', color: theme.palette.success.main }} />
                                    </Box>
                                  </Tooltip>
                                )}
                                {hasProblem && (
                                  <Tooltip title={problemReason} arrow>
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center' }}
                                      aria-label={
                                        region.status === 'cooldown'
                                          ? `Region in cooldown: ${problemReason}`
                                          : `Region unavailable: ${problemReason}`
                                      }
                                    >
                                      {region.status === 'unavailable' ? (
                                        <ErrorOutlineIcon
                                          sx={{
                                            fontSize: 16,
                                            color: theme.palette.error.main,
                                          }}
                                        />
                                      ) : (
                                        <WarningIcon
                                          sx={{
                                            fontSize: 16,
                                            color: theme.palette.warning.main,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>
                                )}
                                {region.region_code || region.id}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1) },
                            }}
                            onClick={() => {
                              const name = region.provider?.name || 'Unknown';
                              if (name) filterBarRef.current?.addFilter(`provider_name="${name}"`, name);
                            }}
                          >
                            <Tooltip title={isFilterActive(`provider_name="${region.provider?.name || 'Unknown'}"`) ? 'Click to remove from filter' : 'Click to add as filter'} arrow>
                              <Box>
                                <Typography
                                  sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    fontSize: '0.85rem',
                                    color: 'text.primary',
                                    ...(isFilterActive(`provider_name="${region.provider?.name || 'Unknown'}"`)
                                      ? { fontWeight: 700, color: theme.palette.primary.main }
                                      : {}),
                                  }}
                                >
                                  {region.provider?.name || 'Unknown'}
                                </Typography>
                                <Chip
                                  label={region.provider?.provider_type || 'N/A'}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    fontFamily: '"Space Mono", monospace',
                                    mt: 0.5,
                                    backgroundColor: alpha(theme.palette.success.main, isDark ? 0.1 : 0.16),
                                    color: theme.palette.success.main,
                                    border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.3 : 0.35)}`,
                                    ...(isFilterActive(`provider_type="${region.provider?.provider_type}"`)
                                      ? { fontWeight: 700, color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}` }
                                      : {}),
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const type = region.provider?.provider_type;
                                    if (type) filterBarRef.current?.addFilter(`provider_type="${type}"`, type.toUpperCase());
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1) },
                              ...(isFilterActive(`bots>=${runningBots}`) ? { fontWeight: 700 } : {}),
                            }}
                            onClick={() => {
                              filterBarRef.current?.addFilter(`bots>=${runningBots}`, `Bots ≥ ${runningBots}`);
                            }}
                          >
                            <Tooltip title={isFilterActive(`bots>=${runningBots}`) ? 'Click to remove from filter' : 'Click to add as filter'} arrow>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Tooltip title={`Running & attacking bots: ${runningBots}`} arrow>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CircleIcon
                                    sx={{
                                      fontSize: '0.6rem',
                                      color: theme.palette.success.main,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontFamily: '"JetBrains Mono", monospace',
                                      color: theme.palette.success.main,
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {runningBots}
                                  </Typography>
                                </Box>
                              </Tooltip>
                              <Tooltip title={`Total capacity (${capacityBasis}): ${capacityLabel}`} arrow>
                                <Typography
                                  sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    color: 'text.secondary',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  / {capacityLabel}
                                </Typography>
                              </Tooltip>
                            </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default RegionsPanel;

